/**
 * Makro sekmesi — Fed fiyatlaması.
 *  - Toplantı merdiveni: her FOMC için artırım/sabit/indirim olasılığı ve değişimi
 *  - Seçili toplantının olasılık geçmişi (günlük uzlaşma + bugünün seans içi noktaları)
 *  - Ham girdiler: rakamın hangi fiyattan geldiği (denetlenebilirlik için bilerek görünür)
 *  - Yaklaşan makro olaylar (TSİ): konsensüs beklentisi, önceki, gerçekleşen ve sürpriz yönü
 *  - Ekonomik takvim: tüm ABD takvimi, önem derecesine göre süzülebilir (ikinci kademe
 *    makro + haftalık yüksek frekanslılar); veri zaten macro.db'de, ek dış istek yok
 * Yöntem backend'de: fedwatch.py (CME FedWatch mantığı, test_fedwatch.py ile sabitlendi).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useWS } from '../WSContext';
import {
  useFedSnapshot, fmtMeeting, fmtTsi, fmtVal, netP,
  type FedEventItem, type FedHistory, type FedMeeting, type FedSnapshot,
} from '../lib/fedwatch';

const card = 'bg-[#061530] border border-[#0a2560] rounded-xl';
const head = 'text-[10px] uppercase tracking-widest text-blue-400 font-bold';

function sgn(v: number | null, digits = 0) {
  if (v === null || v === undefined) return <span className="text-blue-800">—</span>;
  if (Math.abs(v) < 0.5) return <span className="text-blue-600">0</span>;
  return <span className={v > 0 ? 'text-amber-400' : 'text-emerald-400'}>{v > 0 ? '+' : ''}{v.toFixed(digits)}</span>;
}

// ── Toplantı merdiveni ─────────────────────────────────────────────────────────

function Ladder({ meetings, selected, onSelect }: {
  meetings: FedMeeting[]; selected: string | null; onSelect: (m: string) => void;
}) {
  const any50 = meetings.some(m => m.p_hike50 > 0.005 || m.p_cut50 > 0.005);
  return (
    <div className={`${card} p-4`}>
      <div className="flex items-baseline justify-between mb-3">
        <span className={head}>Toplantı merdiveni</span>
        <span className="text-[10px] text-blue-700">satıra tıkla → grafikte göster · 1G/1H: puan, önceki uzlaşmaya göre</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-blue-500 text-right">
            <th className="text-left font-semibold pb-2">Toplantı</th>
            <th className="font-semibold pb-2">Kalan</th>
            <th className="font-semibold pb-2">Beklenen oran</th>
            <th className="font-semibold pb-2">Δ bp</th>
            <th className="font-semibold pb-2">Artırım</th>
            {any50 && <th className="font-semibold pb-2">50bp</th>}
            <th className="font-semibold pb-2">Sabit</th>
            <th className="font-semibold pb-2">İndirim</th>
            <th className="font-semibold pb-2" title="Başlangıç oranından bu toplantı sonuna kadar kaç 25bp adım">Kümülatif</th>
            <th className="font-semibold pb-2">1G</th>
            <th className="font-semibold pb-2">1H</th>
            <th className="text-left font-semibold pb-2 pl-4">Kaynak</th>
          </tr>
        </thead>
        <tbody>
          {meetings.map(m => {
            const sel = m.meeting === selected;
            return (
              <tr key={m.meeting} onClick={() => onSelect(m.meeting)}
                className={`cursor-pointer border-t border-[#0a2560]/60 text-right font-mono tabular-nums ${sel ? 'bg-[#0a2560]/60' : 'hover:bg-[#0a2560]/30'} ${m.thin ? 'opacity-50' : ''}`}>
                <td className="text-left py-1.5 font-sans">
                  <span className="text-white font-semibold">{fmtMeeting(m.meeting)}</span>
                  {m.sep && <span className="ml-1.5 text-[9px] px-1 rounded bg-blue-900/60 text-blue-300" title="Özet Ekonomik Projeksiyon">SEP</span>}
                </td>
                <td className="text-blue-300">{m.days_to}g</td>
                <td className="text-blue-100">{m.end_rate.toFixed(3)}%</td>
                <td>{sgn(m.exp_change_bp, 1)}</td>
                <td className={m.p_hike >= 0.5 ? 'text-amber-300 font-bold' : 'text-blue-100'}>%{(m.p_hike * 100).toFixed(0)}</td>
                {any50 && <td className="text-blue-300">%{(Math.max(m.p_hike50, m.p_cut50) * 100).toFixed(0)}</td>}
                <td className={m.p_hold >= 0.5 ? 'text-white font-bold' : 'text-blue-100'}>%{(m.p_hold * 100).toFixed(0)}</td>
                <td className={m.p_cut >= 0.5 ? 'text-emerald-300 font-bold' : 'text-blue-100'}>%{(m.p_cut * 100).toFixed(0)}</td>
                <td className="text-blue-200">{m.cum_moves >= 0 ? '+' : ''}{m.cum_moves.toFixed(2)}</td>
                <td>{sgn(m.d1)}</td>
                <td>{sgn(m.d7)}</td>
                <td className="text-left pl-4 font-sans text-[10px] text-blue-400 whitespace-nowrap">
                  {m.contract.replace('CBOT:', '')} · {m.method === 'anchor' ? 'çapa' : 'çözüm'} · ±{m.precision_pp}
                  {m.thin && <span className="ml-1.5 text-amber-400" title="Kontrat hacmi düşük — fiyat bayat olabilir">ince</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Olasılık grafiği ───────────────────────────────────────────────────────────

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(e => setW(Math.floor(e[0].contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

type Pt = { t: number; p: number; label: string; intra: boolean };
const RANGES = [{ id: 30, label: '1A' }, { id: 90, label: '3A' }, { id: 0, label: 'Tümü' }] as const;

function ProbChart({ hist, meeting }: { hist: FedHistory | null; meeting: string | null }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const [range, setRange] = useState<number>(90);
  const [hover, setHover] = useState<Pt | null>(null);
  const H = 250, padL = 40, padR = 14, padT = 14, padB = 44;

  const pts: Pt[] = useMemo(() => {
    if (!hist || hist.meeting !== meeting) return [];
    const now = Date.now() / 1000;
    const settle = hist.settle.filter(s => !range || s.t >= now - range * 86400);
    const last = settle.length ? settle[settle.length - 1].t : 0;
    return [
      ...settle.map(s => ({ t: s.t, p: s.p, label: `${fmtMeeting(s.d)} uzlaşma`, intra: false })),
      ...hist.intraday.filter(i => i.t > last).map(i => ({ t: i.t, p: i.p, label: `${fmtTsi(i.t)} TSİ`, intra: true })),
    ];
  }, [hist, meeting, range]);

  const body = (() => {
    if (!W) return null;
    if (pts.length < 2) return <div className="h-[250px] flex items-center justify-center text-xs text-blue-700">Bu toplantı için yeterli geçmiş yok</div>;
    const t0 = pts[0].t, t1 = Math.max(pts[pts.length - 1].t, t0 + 1);
    const vals = pts.map(p => p.p);
    const ymin = Math.min(0, Math.floor(Math.min(...vals) / 25) * 25);
    const ymax = Math.max(100, Math.ceil(Math.max(...vals) / 25) * 25);
    const x = (t: number) => padL + (t - t0) / (t1 - t0) * (W - padL - padR);
    const y = (v: number) => padT + (ymax - v) / (ymax - ymin) * (H - padT - padB);
    const settle = pts.filter(p => !p.intra);
    const intra = pts.filter(p => p.intra);
    const line = settle.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.p).toFixed(1)}`).join(' ');
    const intraLine = intra.length && settle.length
      ? [settle[settle.length - 1], ...intra].map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.p).toFixed(1)}`).join(' ')
      : '';
    const grid: number[] = [];
    for (let v = ymin; v <= ymax; v += 25) grid.push(v);
    const events = (hist?.events ?? []).filter(e => e.t >= t0 && e.t <= t1);
    const nTicks = Math.max(2, Math.min(6, Math.floor(W / 140)));
    const ticks = settle.length > 1
      ? Array.from({ length: nTicks }, (_, i) => settle[Math.round(i * (settle.length - 1) / (nTicks - 1))])
      : [];

    const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      const tx = t0 + (e.clientX - r.left - padL) / (W - padL - padR) * (t1 - t0);
      let best = pts[0];
      for (const p of pts) if (Math.abs(p.t - tx) < Math.abs(best.t - tx)) best = p;
      setHover(best);
    };

    return (
      <svg width={W} height={H} onMouseMove={onMove} onMouseLeave={() => setHover(null)} className="block">
        {grid.map(v => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke={v === 50 ? '#b45309' : '#0a2560'}
              strokeDasharray={v === 50 ? '4 4' : undefined} strokeWidth={1} />
            <text x={padL - 6} y={y(v) + 3} textAnchor="end" fontSize={10} fill="#3b6fb6">%{v}</text>
          </g>
        ))}
        {events.map((e, i) => {
          const ex = x(e.t), right = ex > W - padR - 70;   // sağ kenarda etiket sola yaslanır, kesilmez
          return (
            <g key={`${e.t}-${e.name}`}>
              <line x1={ex} x2={ex} y1={padT} y2={H - padB} stroke={e.name === 'FOMC' ? '#7c3aed' : '#1e3a8a'} strokeWidth={1} />
              <text x={right ? ex - 3 : ex + 3} y={padT + 10 + (i % 3) * 11} textAnchor={right ? 'end' : 'start'}
                fontSize={9} fill={e.name === 'FOMC' ? '#a78bfa' : '#60a5fa'}>{e.name}</text>
            </g>
          );
        })}
        <path d={line} fill="none" stroke="#60a5fa" strokeWidth={1.8} />
        {intraLine && <path d={intraLine} fill="none" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="3 3" />}
        {intra.map(p => <circle key={p.t} cx={x(p.t)} cy={y(p.p)} r={2.2} fill="#fbbf24" />)}
        {ticks.map((p, i) => (
          <text key={`${p.t}-${i}`} x={x(p.t)} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="#3b6fb6">
            {p.label.replace(' uzlaşma', '')}
          </text>
        ))}
        {hover && (
          <g>
            <line x1={x(hover.t)} x2={x(hover.t)} y1={padT} y2={H - padB} stroke="#93c5fd" strokeWidth={1} strokeDasharray="2 2" />
            <circle cx={x(hover.t)} cy={y(hover.p)} r={3.5} fill={hover.intra ? '#fbbf24' : '#60a5fa'} />
            <text x={Math.min(x(hover.t) + 8, W - 170)} y={H - 8} fontSize={11} fill="#e0f2fe">
              {hover.label} · %{hover.p.toFixed(0)}
            </text>
          </g>
        )}
      </svg>
    );
  })();

  return (
    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className={head}>
          {meeting ? `${fmtMeeting(meeting)} — net artırım olasılığı` : 'Olasılık geçmişi'}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-blue-700">
            <span className="text-blue-400">—</span> uzlaşma · <span className="text-amber-400">- -</span> seans içi · <span className="text-amber-600">- -</span> %50
          </span>
          <div className="flex gap-1">
            {RANGES.map(r => (
              <button key={r.id} onClick={() => setRange(r.id)}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold ${range === r.id ? 'bg-[#003087] text-white' : 'text-blue-400 hover:text-blue-200'}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div ref={ref} className="w-full">{body}</div>
      <p className="text-[10px] text-blue-700 mt-1">Artırım pozitif, indirim negatif. Tarihler CME işlem günü; seans içi saatler TSİ.</p>
    </div>
  );
}

// ── Ham girdiler + SOFR kontrolü ───────────────────────────────────────────────

function Inputs({ snap }: { snap: FedSnapshot }) {
  const m = snap.meetings?.[0];
  const chk = m?.sofr_check;
  return (
    <div className={`${card} p-4`}>
      <span className={head}>Ham girdiler</span>
      <div className="mt-2 grid grid-cols-2 gap-x-4 text-[11px] text-blue-300 mb-2">
        <div>Hedef bant <span className="text-white font-mono">{snap.target ? `${snap.target[0].toFixed(2)}–${snap.target[1].toFixed(2)}%` : '—'}</span></div>
        <div>EFFR <span className="text-white font-mono">{snap.r0?.toFixed(2)}%</span> <span className="text-blue-600">({snap.effr_date})</span></div>
        <div>SOFR <span className="text-white font-mono">{snap.sofr?.toFixed(2) ?? '—'}%</span></div>
        <div>Formül <span className="text-blue-200">(ima − EFFR) / 0.25</span></div>
      </div>
      {m && chk !== null && chk !== undefined && (
        <div className="text-[11px] mb-2 px-2 py-1.5 rounded bg-[#0a2560]/40 text-blue-200">
          SOFR kontrolü ({fmtMeeting(m.meeting, false)}): fed funds <span className="font-mono text-white">%{(netP(m)).toFixed(0)}</span>
          {' · '}SR1 <span className="font-mono text-white">%{(chk * 100).toFixed(0)}</span>
          {' · '}fark <span className={`font-mono ${Math.abs(chk * 100 - netP(m)) > 10 ? 'text-amber-400' : 'text-emerald-400'}`}>
            {(chk * 100 - netP(m)).toFixed(0)} puan</span>
          {Math.abs(chk * 100 - netP(m)) > 10 && <span className="text-amber-500"> — SOFR bazı çeyrek sonlarında gürültülü</span>}
        </div>
      )}
      <div className="max-h-56 overflow-auto">
        <table className="w-full text-[11px] font-mono tabular-nums">
          <thead>
            <tr className="text-[10px] text-blue-500 font-sans text-right">
              <th className="text-left font-semibold pb-1">Kontrat</th><th className="font-semibold pb-1">Fiyat</th>
              <th className="font-semibold pb-1">İma</th><th className="font-semibold pb-1">Hacim</th>
            </tr>
          </thead>
          <tbody>
            {(snap.inputs ?? []).map(i => (
              <tr key={i.symbol} className={`text-right border-t border-[#0a2560]/50 ${i.thin ? 'opacity-40' : ''}`}>
                <td className="text-left text-blue-300 py-0.5">{i.symbol.replace(/^(CBOT|CME):/, '')}</td>
                <td className="text-white">{i.price.toFixed(4)}</td>
                <td className="text-blue-200">{i.implied.toFixed(3)}%</td>
                <td className="text-blue-400">{i.volume === null ? '—' : i.volume.toLocaleString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventItem({ it }: { it: FedEventItem }) {
  const cls = it.hawk > 0 ? 'text-amber-400' : it.hawk < 0 ? 'text-emerald-400' : 'text-white';
  const surprise = it.surprise === null ? '' :
    `beklentiden ${it.surprise > 0 ? '+' : ''}${Number(it.surprise.toFixed(3))}${it.unit === '%' ? ' puan' : it.unit}` +
    (it.hawk > 0 ? ' — sıkılaştırma yönünde' : it.hawk < 0 ? ' — gevşeme yönünde' : '');
  return (
    <>
      <span className="text-blue-400 truncate" title={it.period ? `Dönem: ${it.period}` : undefined}>{it.label}</span>
      <span className="text-blue-200 text-right"
        title={it.fc_prev !== null ? `Beklenti revize edildi: ${fmtVal(it.fc_prev, it.unit)} → ${fmtVal(it.forecast, it.unit)} (son 1 hafta)` : 'Konsensüs beklentisi'}>
        {it.fc_prev !== null && <span className={it.forecast! > it.fc_prev ? 'text-amber-500' : 'text-emerald-500'}>{it.forecast! > it.fc_prev ? '↑' : '↓'}</span>}
        {fmtVal(it.forecast, it.unit)}
      </span>
      <span className="text-blue-600 text-right" title="Önceki (revize edilmiş olabilir)">{fmtVal(it.previous, it.unit)}</span>
      <span className={`text-right font-semibold ${it.actual === null ? 'text-blue-800' : cls}`} title={surprise || undefined}>
        {it.actual !== null && it.hawk !== 0 && (it.hawk > 0 ? '▲' : '▼')}{fmtVal(it.actual, it.unit)}
      </span>
    </>
  );
}

function Calendar({ snap }: { snap: FedSnapshot }) {
  const now = Date.now() / 1000;
  return (
    <div className={`${card} p-4`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={head}>Yaklaşan olaylar (TSİ)</span>
        {snap.cal_ts && <span className="text-[10px] text-blue-700" title="Konsensüs: TradingView ekonomik takvimi. Saatte bir; açıklamadan sonra gerçekleşen değer gelene kadar 2 dakikada bir güncellenir.">
          konsensüs {fmtTsi(snap.cal_ts)}</span>}
      </div>
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-x-3 text-[10px] text-blue-700">
        <span /><span className="text-right">Bkl.</span><span className="text-right">Önc.</span><span className="text-right">Açk.</span>
      </div>
      <div className="mt-1 space-y-2.5">
        {(snap.events ?? []).map(e => {
          const past = e.ts < now;
          const days = Math.max(0, Math.round((e.ts - now) / 86400));
          return (
            <div key={`${e.ts}-${e.name}`}>
              <div className="flex items-center justify-between text-xs">
                <span className={e.name === 'FOMC' ? 'text-violet-300 font-semibold' : past ? 'text-blue-400' : 'text-blue-200'}>{e.name}</span>
                <span className="font-mono text-blue-400">{fmtTsi(e.ts)} <span className="text-blue-700">· {past ? 'açıklandı' : days === 0 ? 'bugün' : `${days}g`}</span></span>
              </div>
              {!!e.items?.length && (
                <div className="mt-0.5 pl-2 grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-x-3 text-[11px] font-mono tabular-nums">
                  {e.items.map(it => <EventItem key={it.label} it={it} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Ekonomik takvim (önem süzgeçli) ──────────────────────────────────────────
// Veri ZATEN toplanıyor: backend tüm ABD takvimini macro.db'ye yazıyor, yukarıdaki
// "yaklaşan olaylar" kartı bunun yalnız beş başlığını gösteriyordu. Burası kalanını
// açıyor — ikinci kademe makro (ISM, perakende, ÜFE, GSYH…) ve haftalık yüksek
// frekanslılar (işsizlik başvuruları, EIA stokları, mortgage, Redbook).
// Ayrı WS isteği: anlık görüntü yayın anında saniyede bir yayılıyor, takvimi oraya
// koymak her saniye ~20 KB yaymak olurdu.

type EconRow = {
  id: string; ts: number; title: string; label: string; period: string | null;
  actual: number | null; forecast: number | null; previous: number | null;
  importance: number; fc_prev: number | null; surprise: number | null;
};
type EconFeed = { rows: EconRow[]; counts: Record<string, number> };

/** Değerler çok farklı ölçekte geliyor (4.085 · 97.9 · 23.75 · 3.99) — sabit
 *  ondalık biri gürültü, diğeri bilgi kaybı olurdu. */
function num(v: number | null): string {
  if (v === null || v === undefined) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return v.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
  return String(Number(v.toFixed(a < 10 ? 3 : 2)));
}

const ONEM = [
  { id: 1,  etiket: 'Yüksek',       nokta: 'bg-rose-400' },
  { id: 0,  etiket: 'Orta ve üstü', nokta: 'bg-amber-400' },
  { id: -1, etiket: 'Tümü',         nokta: 'bg-blue-600' },
];

function EconCalendar({ calTs }: { calTs?: number | null }) {
  const { sendWs, subscribe, connected } = useWS();
  const [feed, setFeed] = useState<EconFeed | null>(null);
  const [imp, setImp] = useState<number>(() => {
    const v = Number(localStorage.getItem('bq_macro_importance'));
    return [1, 0, -1].includes(v) ? v : 0;      // varsayılan: orta ve üstü
  });

  useEffect(() => subscribe((msg: any) => {
    if (msg?.type === 'econ_cal' && msg.payload) setFeed(msg.payload as EconFeed);
  }), [subscribe]);

  // Süzgeç değişince ve takvim damgası tazelenince yeniden iste.
  useEffect(() => {
    if (!connected) return;
    try { localStorage.setItem('bq_macro_importance', String(imp)); } catch {}
    sendWs({ type: 'get_econ_cal', payload: { days_back: 2, days_fwd: 21, importance: imp } });
  }, [connected, imp, calTs, sendWs]);

  const sayim = feed?.counts ?? {};
  const topla = (min: number) => [1, 0, -1]
    .filter(k => k >= min).reduce((t, k) => t + (sayim[String(k)] ?? 0), 0);

  const now = Date.now() / 1000;
  // Güne göre grupla: 250 satırı düz liste yapmak taranamaz olurdu.
  const gunler = useMemo(() => {
    const m = new Map<string, EconRow[]>();
    for (const r of feed?.rows ?? []) {
      const g = new Date(r.ts * 1000).toLocaleDateString('tr-TR',
        { weekday: 'short', day: '2-digit', month: 'short' });
      (m.get(g) ?? m.set(g, []).get(g)!).push(r);
    }
    return [...m.entries()];
  }, [feed]);

  return (
    <div className={`${card} p-4`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className={head}>Ekonomik takvim (TSİ)</span>
        <div className="flex items-center gap-1">
          {ONEM.map(o => (
            <button key={o.id} onClick={() => setImp(o.id)}
              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                imp === o.id ? 'bg-[#003087] text-white' : 'text-blue-400 hover:text-blue-200'}`}>
              {o.etiket} <span className="opacity-60">{topla(o.id)}</span>
            </button>
          ))}
        </div>
      </div>

      {!feed ? (
        <div className="mt-3 text-xs text-blue-600">Takvim yükleniyor…</div>
      ) : !feed.rows.length ? (
        <div className="mt-3 text-xs text-blue-600">Bu önem düzeyinde kayıt yok.</div>
      ) : (
        <div className="mt-2 space-y-3">
          {gunler.map(([gun, satirlar]) => (
            <div key={gun}>
              <div className="text-[10px] uppercase tracking-wider text-blue-600 border-b border-[#0a2560] pb-1">
                {gun}
              </div>
              <div className="mt-1 space-y-0.5">
                {satirlar.map(r => {
                  const gecti = r.ts < now;
                  const srp = r.surprise;
                  return (
                    <div key={r.id}
                      className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto] gap-x-3 items-baseline text-[11px]">
                      <span className={`font-mono tabular-nums ${gecti ? 'text-blue-700' : 'text-blue-400'}`}>
                        {fmtTsi(r.ts)}
                      </span>
                      {/* Önem noktası: renk yalnız sınıfı anlatır, yorum katmaz */}
                      <span className={`w-1.5 h-1.5 rounded-full self-center ${
                        r.importance >= 1 ? 'bg-rose-400' : r.importance === 0 ? 'bg-amber-400' : 'bg-blue-800'}`} />
                      <span className={`truncate ${gecti ? 'text-blue-500' : 'text-blue-200'}`}
                        title={`${r.title}${r.period ? ' · ' + r.period : ''}`}>
                        {r.label}
                        {r.period && <span className="ml-1 text-blue-700">{r.period}</span>}
                      </span>
                      <span className="font-mono tabular-nums text-right text-blue-400"
                        title={r.fc_prev !== null ? `Beklenti revize edildi: ${num(r.fc_prev)} → ${num(r.forecast)}` : undefined}>
                        {num(r.forecast)}{r.fc_prev !== null && <span className="text-amber-500">*</span>}
                      </span>
                      <span className="font-mono tabular-nums text-right text-blue-600">{num(r.previous)}</span>
                      {/* Gerçekleşen: beklentiden sapma renklendiriliyor ama ŞAHİN/GÜVERCİN
                          YORUMU YAPILMIYOR — yön göstergeye göre değişir (işsizlikte yüksek
                          gelmek gevşek, enflasyonda sıkı) ve ~100 başlık için elle işaret
                          tanımlamak bakım yükü olurdu. */}
                      <span className={`font-mono tabular-nums text-right ${
                        r.actual === null ? 'text-blue-800'
                          : srp === null || srp === 0 ? 'text-white'
                          : srp > 0 ? 'text-emerald-300' : 'text-rose-300'}`}
                        title={srp === null ? undefined : `Beklentiye göre ${srp > 0 ? '+' : ''}${num(srp)}`}>
                        {num(r.actual)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto] gap-x-3 text-[10px] text-blue-700">
        <span /><span /><span />
        <span className="text-right">Beklenti</span><span className="text-right">Önceki</span>
        <span className="text-right">Açıklanan</span>
      </div>
    </div>
  );
}

// ── Sekme ──────────────────────────────────────────────────────────────────────

export default function MacroPanel() {
  const { sendWs, subscribe, connected } = useWS();
  const snap = useFedSnapshot();
  const [hist, setHist] = useState<FedHistory | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const meetings = snap?.meetings ?? [];
  const active = selected ?? meetings[0]?.meeting ?? null;

  useEffect(() => subscribe((msg: any) => {
    if (msg?.type === 'fed_history' && msg.payload) setHist(msg.payload as FedHistory);
  }), [subscribe]);

  // Seçim değişince veya yeni ölçüm geldiğinde (bugünün seans içi noktası eklensin) geçmişi iste
  useEffect(() => {
    if (!connected || !active) return;
    sendWs({ type: 'get_fed_history', payload: { meeting: active, days: 400 } });
  }, [connected, active, snap?.ts, sendWs]);

  if (!snap) {
    return <div className="flex-1 flex items-center justify-center text-sm text-blue-600">Fed verisi yükleniyor…</div>;
  }
  if (!snap.ready) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-blue-600">
        Henüz hesaplanmış veri yok — sunucu ilk açılışta geçmişi dolduruyor (~2 dk).
        {snap.error && <span className="ml-2 text-amber-500">{snap.error}</span>}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
      <div className="flex items-center gap-4 text-xs text-blue-400">
        <span>Son ölçüm <span className="text-white font-mono">{snap.ts ? fmtTsi(snap.ts) : '—'} TSİ</span>
          <span className="text-blue-600"> · {snap.kind === 'settle' ? `uzlaşma (${fmtMeeting(snap.trade_date!)})` : 'seans içi, 10 dk gecikmeli'}</span></span>
        {snap.stale && <span className="flex items-center gap-1 text-amber-400"><AlertTriangle size={12} /> veri 30 dakikadan eski</span>}
        {snap.r0_stale && <span className="flex items-center gap-1 text-amber-400"><AlertTriangle size={12} /> EFFR son FOMC kararını henüz yansıtmıyor — ilk toplantının başlangıç oranı eski</span>}
      </div>

      <Ladder meetings={meetings} selected={active} onSelect={setSelected} />

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2"><ProbChart hist={hist} meeting={active} /></div>
        <div className="space-y-4">
          <Calendar snap={snap} />
        </div>
      </div>

      <EconCalendar calTs={snap.cal_ts} />

      <Inputs snap={snap} />

      <p className="text-[10px] text-blue-800">
        Kaynak: TradingView (CBOT ZQ fed funds, CME SR1 1 aylık SOFR; 10 dk gecikmeli) · NY Fed (EFFR, SOFR) ·
        TradingView ekonomik takvimi (konsensüs; tek kaynaklı toplulaştırma, Bloomberg anket medyanından farklı olabilir).
        Yöntem: toplantıdan sonraki toplantısız ayın kontratı çapa; yoksa toplantı ayının kontratından çözüm.
        Olasılık = beklenen değişim / 25bp. ±: yarım baz puanlık tikin olasılık karşılığı.
      </p>
    </div>
  );
}
